import React, { useState, useEffect, useCallback } from 'react';
import { getSelectionAsBase64, insertFragmentFromBase64, insertFloatingPlaceholder } from '../services/wordHelper';
import { getAuthHeaders, API_URL } from '../services/auth';
import './FragmentPanel.css';

interface Fragment {
  id: number;
  category: string;
  standardText: string;
  description: string | null;
  riskLevel: string;
  isActive: boolean;
  contentType: string;
  docxContent: string | null;
  docxSize: number;
  usedTags: string[];
  version: number;
  versionStatus: string;
  createdAt: string;
  updatedAt: string;
}

type TabId = 'browse' | 'save';

const CATEGORIES = ['Headers', 'Footers', 'Clauses', 'Disclaimers', 'Signatures', 'Boilerplate'];

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(options?.headers || {}),
    },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  return res.json();
}

export function FragmentPanel() {
  const [tab, setTab] = useState<TabId>('browse');
  const [fragments, setFragments] = useState<Fragment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Save tab state
  const [saveName, setSaveName] = useState('');
  const [saveCategory, setSaveCategory] = useState(CATEGORIES[0]);
  const [saveDescription, setSaveDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const fetchFragments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await apiFetch('/clause-library');
      setFragments(json.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load fragments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'browse') fetchFragments();
  }, [tab, fetchFragments]);

  const handleInsert = async (fragment: Fragment) => {
    try {
      // Drop a chip referencing the fragment by a slug of its name. At
      // generate time, the server expands this chip with the fragment's
      // CURRENT content — so edits to the library propagate automatically.
      const name = fragment.standardText || `Fragment`;
      const slug = name.trim().replace(/[^a-zA-Z0-9]/g, '') || String((fragment as any).id || (fragment as any).Id);
      const tag = `fragment.${slug}`;
      await insertFloatingPlaceholder(tag, name, 'Text');
      setMessage({
        type: 'success',
        text: `Inserted reference to "${name.slice(0, 30)}". Updates to this fragment will auto-apply at generation.`,
      });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to insert fragment' });
    }
  };

  const handleSave = async () => {
    if (!saveName.trim()) {
      setMessage({ type: 'error', text: 'Please enter a fragment name' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      let base64: string | null = null;
      try { base64 = await getSelectionAsBase64(); } catch { /* no selection */ }

      if (editingId) {
        // Update existing fragment
        await apiFetch(`/clause-library/${editingId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            category: saveCategory,
            standardText: saveName.trim(),
            description: saveDescription.trim() || undefined,
            ...(base64 ? { contentType: 'docx', docxContent: base64 } : {}),
          }),
        });
        setMessage({ type: 'success', text: `Fragment updated successfully` });
      } else {
        // Create new fragment
        await apiFetch('/clause-library', {
          method: 'POST',
          body: JSON.stringify({
            category: saveCategory,
            standardText: saveName.trim(),
            description: saveDescription.trim() || undefined,
            ...(base64 ? { contentType: 'docx', docxContent: base64 } : {}),
          }),
        });
        setMessage({ type: 'success', text: `Fragment "${saveName}" saved successfully` });
      }
      setSaveName('');
      setSaveDescription('');
      setEditingId(null);
      fetchFragments();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to save fragment' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (f: any) => {
    setEditingId(f.id || f.Id);
    setSaveName(f.standardText || f.StandardText || '');
    setSaveCategory(f.category || f.Category || CATEGORIES[0]);
    setSaveDescription(f.description || f.Description || '');
    setTab('save');

    // Insert the current fragment content into the document so the user can
    // see and modify it. When they re-select the edited content and click
    // Update Fragment, handleSave reads the new selection and PATCHes
    // docxContent alongside the metadata.
    if (f.docxContent) {
      try {
        await insertFragmentFromBase64(f.docxContent);
        setMessage({
          type: 'success',
          text: 'Fragment inserted — edit it in Word, then select the updated content and click Update Fragment.',
        });
      } catch (err: any) {
        setMessage({ type: 'error', text: err.message || 'Failed to load fragment content for editing' });
      }
    } else {
      setMessage({
        type: 'error',
        text: 'No content available — you can update the details but not the content.',
      });
    }
  };

  const filtered = fragments.filter((f) => {
    if (categoryFilter && f.category.toLowerCase() !== categoryFilter.toLowerCase()) return false;
    if (search) {
      const q = search.toLowerCase();
      return f.standardText.toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q);
    }
    return true;
  });

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="dp-frag">
      {/* Tabs */}
      <div className="dp-frag__tabs">
        <button
          className={`dp-frag__tab${tab === 'browse' ? ' dp-frag__tab--active' : ''}`}
          onClick={() => setTab('browse')}
        >
          Browse
        </button>
        <button
          className={`dp-frag__tab${tab === 'save' ? ' dp-frag__tab--active' : ''}`}
          onClick={() => setTab('save')}
        >
          Save
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className={`dp-frag__message dp-frag__message--${message.type}`}>
          {message.text}
          <button className="dp-frag__message-close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {/* Browse Tab */}
      {tab === 'browse' && (
        <div className="dp-frag__browse">
          <input
            type="text"
            className="dp-frag__search"
            placeholder="Search fragments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="dp-frag__chips">
            <button
              className={`dp-frag__chip${!categoryFilter ? ' dp-frag__chip--active' : ''}`}
              onClick={() => setCategoryFilter(null)}
            >
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`dp-frag__chip${categoryFilter === cat ? ' dp-frag__chip--active' : ''}`}
                onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading && <div className="dp-frag__loading">Loading fragments...</div>}
          {error && <div className="dp-frag__error">{error}</div>}

          {!loading && !error && filtered.length === 0 && (
            <div className="dp-frag__empty">No fragments found. Save a selection to create one.</div>
          )}

          <div className="dp-frag__list">
            {filtered.map((f) => (
              <div key={f.id} className="dp-frag__card">
                <div className="dp-frag__card-header">
                  <span className="dp-frag__card-name">{f.standardText}</span>
                  <span className="dp-frag__card-badge">{f.category}</span>
                </div>
                {f.description && (
                  <div className="dp-frag__card-desc">{f.description}</div>
                )}
                <div className="dp-frag__card-meta">
                  <span>{formatSize(f.docxSize)}</span>
                  <span className="dp-frag__card-version">v{f.version}</span>
                </div>

                {previewId === f.id && (
                  <div className="dp-frag__preview">
                    <div className="dp-frag__preview-label">Text Preview:</div>
                    <div className="dp-frag__preview-text">{f.standardText}</div>
                  </div>
                )}

                <div className="dp-frag__card-actions">
                  <button className="dp-frag__btn dp-frag__btn--primary" onClick={() => handleInsert(f)}>
                    Insert
                  </button>
                  <button
                    className="dp-frag__btn dp-frag__btn--secondary"
                    onClick={() => setPreviewId(previewId === f.id ? null : f.id)}
                  >
                    {previewId === f.id ? 'Hide' : 'Preview'}
                  </button>
                  <button
                    className="dp-frag__btn dp-frag__btn--secondary"
                    onClick={() => handleEdit(f)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Tab */}
      {tab === 'save' && (
        <div className="dp-frag__save">
          <div style={{ background: editingId ? '#fef3c7' : '#eff6ff', border: editingId ? '1px solid #fde68a' : '1px solid #bfdbfe', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: editingId ? '#92400e' : '#1e40af', lineHeight: 1.5 }}>
            {editingId ? (
              <>
                <strong>Editing fragment:</strong>
                <ol style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  <li>The fragment has been inserted into your Word document</li>
                  <li>Edit the content in Word (text, formatting, placeholders)</li>
                  <li><strong>Select the updated content</strong></li>
                  <li>Adjust name / category / description if needed</li>
                  <li>Click <strong>Update Fragment</strong> to save the new content</li>
                </ol>
              </>
            ) : (
              <>
                <strong>How to create a fragment:</strong>
                <ol style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  <li>Type and format your content in the <strong>Word document</strong> (use bold, fonts, tables, placeholders)</li>
                  <li><strong>Select</strong> the content you want to save</li>
                  <li>Enter a name and category below</li>
                  <li>Click <strong>Save Selection as Fragment</strong></li>
                </ol>
              </>
            )}
          </div>

          <label className="dp-frag__label">Name</label>
          <input
            type="text"
            className="dp-frag__input"
            placeholder="e.g. Indemnitor Block"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
          />

          <label className="dp-frag__label">Category</label>
          <select
            className="dp-frag__select"
            value={saveCategory}
            onChange={(e) => setSaveCategory(e.target.value)}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <label className="dp-frag__label">Description (optional)</label>
          <input
            type="text"
            className="dp-frag__input"
            placeholder="Brief description of this fragment"
            value={saveDescription}
            onChange={(e) => setSaveDescription(e.target.value)}
          />

          <button
            className="dp-frag__btn dp-frag__btn--primary dp-frag__btn--full"
            onClick={handleSave}
            disabled={saving || !saveName.trim()}
            style={{ marginTop: 10 }}
          >
            {saving ? 'Saving...' : editingId ? 'Update Fragment' : 'Save Selection as Fragment'}
          </button>

          {message && (
            <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 4, fontSize: 11,
              background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
              color: message.type === 'success' ? '#166534' : '#991b1b',
              border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
              {message.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
